/* =====================================================================
 * skillPagination — 스킬 카드 목록의 클라이언트 페이지네이션
 *
 * 마켓플레이스 페이지의 세 탭(내 스킬 · 레퍼런스 · 공유)이 같은 페이저를
 * 쓴다. 목록은 이미 전부 받아 두므로(API 상한 500) 서버 페이징 없이
 * 잘라 보여 주기만 한다. 모달은 `pageSize` 를 넘기지 않아 예전처럼 전부
 * 그린다.
 *
 * 모양은 에이전트 메모리 화면의 페이저(`memory.styles.ts` Pagination /
 * PageButton / PageStatus)와 같은 골격 — 이전 · "n / m 페이지" · 다음.
 * ===================================================================== */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface PagedList<T> {
  /** 1부터 시작. 목록이 줄면 마지막 페이지로 조여진 값. */
  page: number;
  pageCount: number;
  pageItems: T[];
  setPage: (page: number) => void;
}

/**
 * `pageSize` 가 없으면 페이징하지 않는다(모달). `resetKey` 가 바뀌면(검색어·
 * 카테고리) 1페이지로 돌아간다. 삭제 등으로 목록이 줄어 현재 페이지가 비면
 * 마지막 페이지로 조인다 — 빈 페이지를 보여 주지 않는다.
 */
export function usePagedList<T>(
  items: readonly T[],
  pageSize?: number,
  resetKey?: unknown,
): PagedList<T> {
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  const size = pageSize && pageSize > 0 ? pageSize : undefined;
  const pageCount = size ? Math.max(1, Math.ceil(items.length / size)) : 1;
  const current = Math.min(Math.max(1, page), pageCount);
  const pageItems = size
    ? items.slice((current - 1) * size, current * size)
    : [...items];
  return { page: current, pageCount, pageItems, setPage };
}

interface SkillPagerProps {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}

/** 한 페이지뿐이면 아무것도 그리지 않는다. */
export const SkillPager: React.FC<SkillPagerProps> = ({ page, pageCount, onChange }) => {
  const { t } = useTranslation('common');
  if (pageCount <= 1) return null;
  return (
    <Pagination aria-label={t('skill.pagination.label')}>
      <PageButton
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(Math.max(1, page - 1))}
        aria-label={t('skill.pagination.previous')}
      >
        <ChevronLeft aria-hidden="true" />
        <span>{t('skill.pagination.previous')}</span>
      </PageButton>
      <PageStatus aria-live="polite">
        {t('skill.pagination.status', { current: page, total: pageCount })}
      </PageStatus>
      <PageButton
        type="button"
        disabled={page >= pageCount}
        onClick={() => onChange(Math.min(pageCount, page + 1))}
        aria-label={t('skill.pagination.next')}
      >
        <span>{t('skill.pagination.next')}</span>
        <ChevronRight aria-hidden="true" />
      </PageButton>
    </Pagination>
  );
};

/* ------------------------------------------------------------------ */
/* Styles — 색·간격·라운드는 테마 토큰 경유                               */
/* ------------------------------------------------------------------ */

const Pagination = styled.nav`
  display: grid;
  grid-template-columns: minmax(96px, 1fr) auto minmax(96px, 1fr);
  align-items: center;
  gap: 16px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr auto 1fr;
    gap: 8px;
  }
`;

const PageButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 32px;
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast};

  &:first-child {
    justify-self: start;
  }

  &:last-child {
    justify-self: end;
  }

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.borderInput};
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }

  /* GlobalStyle 이 button outline 을 지우므로 키보드 포커스 링을 직접 그린다. */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brand};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const PageStatus = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  text-align: center;
`;
